import { AsyncPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { remove } from "lodash";
import { GridSelectState } from "rl-common/components/grid/models/grid-select-state";
import { IEntitySearchDoc } from "rl-common/models/i-entity-search-doc";
import { IRecordTitle } from "rl-common/models/i-record-title";
import { PluralCharTypeNamePipe } from "rl-common/pipes/plural-char-type-name.pipe";
import { CharTypeId } from "rl-common/rl-common.consts";
import { IGridViewAssocEntityRec } from "rl-common/services/grid-view/models/i-grid-view-assoc-entity-rec";
import { combineLatest, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { ChipsComponent, IChipDeselectedEvent } from "../../../../chip/chips/chips.component";
import { SearchGridDataSource } from "../../../../grid/datasource/search-grid.datasource";

@Component({
	selector: "rl-association-chips",
	templateUrl: "./association-chips.component.html",
	styleUrls: ["./association-chips.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ChipsComponent, AsyncPipe, PluralCharTypeNamePipe]
})
export class AssociationChipsComponent implements OnInit {
	@Input()
	dataSource: SearchGridDataSource<IEntitySearchDoc>;

	@Input()
	charTypeId: CharTypeId;

	@Input()
	newRecordTitle: IRecordTitle;

	@Input()
	selectedInputList: IEntitySearchDoc[] = [];

	@Input()
	showId: boolean = false;

	@Output()
	onNewRelRemoved = new EventEmitter<void>();
	isEmpty$: Observable<boolean>;
	selectedCount$: Observable<number>;

	gridChipIdFn = (chip: IGridViewAssocEntityRec) => chip.recordId;
	gridChipDisplayFn = (chip: IGridViewAssocEntityRec) => this.showId ? chip.recordId : chip.title;
	inputListChipIdFn = (chip: IRecordTitle) => chip.recordId;
	inputListChipDisplayFn = (chip: IRecordTitle) => this.showId ? chip.recordId : chip.title;

	constructor() { }

	ngOnInit(): void {
		if (!this.dataSource) {
			console.warn(`No dataSource supplied.`);
		}

		// RL-5320/RL-7625/RL-7626: Use strategy's selectCounts$ if available (EntityRelationshipDataSelectStrategy)
		// to get the adjusted count that excludes inactive Catalogs when feature flag is enabled
		const strategy = this.dataSource.dataSelectStrategy as any;
		if (strategy?.selectCounts$) {
			this.selectedCount$ = combineLatest([
				strategy.selectStateChange$ as Observable<GridSelectState<number, unknown>>,
				strategy.selectCounts$ as Observable<[number, number, number, number]>
			]).pipe(
				map(([state, counts]) => {
					const newCount = this.newRecordTitle ? 1 : 0;
					let count = 0;
					if (!state.isAllSelected) {
						count = state.selectedIds.size;
					} else if (counts?.[3] !== null && counts?.[3] !== undefined) {
						count = counts[3];
					} else {
						const rowCount = this.dataSource.availableRowCount$.value ?? this.dataSource.rowCount$.value;
						count = rowCount - state.deselectedIds.size;
					}
					return newCount + count;
				})
			);
		} else {

			this.selectedCount$ = this.dataSource.dataSelectStrategy.selectStateChange$
				.pipe(map(state => {
					const newCount = this.newRecordTitle ? 1 : 0;
					let count = 0;
					if (!state.isAllSelected) {
						count = state.selectedIds.size;
					} else {
						const deselectedCount = state.deselectedIds.size;
						const rowCount = this.dataSource.availableRowCount$.value ?? this.dataSource.rowCount$.value;
						count = rowCount - deselectedCount;
					}
					return newCount + count;
				}));
		}
		this.isEmpty$ = this.selectedCount$.pipe(map(count => count === 0));
	}

	deselect(event: IChipDeselectedEvent<number, unknown>) {
		if (event.isNewRow) {
			this.onNewRelRemoved.emit();
		} else if (event.isSelectAll) {
			this.dataSource.dataSelectStrategy.deselectAll();
		} else {
			this.dataSource.dataSelectStrategy.deselectRowById(event.id, event.value);
		}
	}

	deselectInputList(event: IChipDeselectedEvent<number, IEntitySearchDoc>) {
		this.dataSource.dataSelectStrategy.deselectRowById(event.value.recordID, event.value);
		remove(this.selectedInputList, i => i.recordID === event.value.recordID);
	}
}
